import { useState, useRef, useEffect } from 'react';
import { LeaderboardModal } from './Leaderboard';
import { ProfileModal } from './ProfileModal';

const useWindowSize = () => {
  const [windowSize, setWindowSize] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 0,
  });

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return windowSize;
};

const AnimatedHubIcon = ({ isOpen, ...props }: React.SVGProps<SVGSVGElement> & { isOpen: boolean }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <rect 
      x="3" y="3" width="7" height="7" 
      style={{
        transform: isOpen ? 'translate(-1px, -1px)' : 'translate(0, 0)',
        transition: 'transform 0.3s ease-out'
      }}
    />
    <rect 
      x="14" y="3" width="7" height="7"
      style={{
        transform: isOpen ? 'translate(1px, -1px)' : 'translate(0, 0)',
        transition: 'transform 0.3s ease-out'
      }}
    />
    <rect 
      x="14" y="14" width="7" height="7"
      style={{
        transform: isOpen ? 'translate(1px, 1px)' : 'translate(0, 0)',
        transition: 'transform 0.3s ease-out'
      }}
    />
    <rect 
      x="3" y="14" width="7" height="7"
      style={{
        transform: isOpen ? 'translate(-1px, 1px)' : 'translate(0, 0)',
        transition: 'transform 0.3s ease-out'
      }}
    />
  </svg>
);

const AnimatedUserIcon = ({ isAnimated, ...props }: React.SVGProps<SVGSVGElement> & { isAnimated?: boolean }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path 
      d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"
      style={{
        transform: isAnimated ? 'scaleY(1.2)' : 'scaleY(1)',
        transformOrigin: 'bottom',
        transition: 'transform 0.3s ease-out'
      }}
    />
    <circle 
      cx="12" cy="7" r="4"
      style={{
        transform: isAnimated ? 'scale(1.2)' : 'scale(1)',
        transformOrigin: 'center',
        transition: 'transform 0.3s ease-out'
      }}
    />
  </svg>
);

const AnimatedLeaderboardIcon = ({ isAnimated, ...props }: React.SVGProps<SVGSVGElement> & { isAnimated?: boolean }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    {/* Столбчатая диаграмма для лидерборда */}
    <rect 
      x="3" y="12" width="4" height="9" rx="1"
      style={{
        transform: isAnimated ? 'scaleY(1.3)' : 'scaleY(1)',
        transformOrigin: 'bottom',
        transition: 'transform 0.3s ease-out 0.1s'
      }}
    />
    <rect 
      x="10" y="7" width="4" height="14" rx="1"
      style={{
        transform: isAnimated ? 'scaleY(1.3)' : 'scaleY(1)',
        transformOrigin: 'bottom',
        transition: 'transform 0.3s ease-out'
      }}
    />
    <rect 
      x="17" y="15" width="4" height="6" rx="1"
      style={{
        transform: isAnimated ? 'scaleY(1.3)' : 'scaleY(1)',
        transformOrigin: 'bottom',
        transition: 'transform 0.3s ease-out 0.2s'
      }}
    />
  </svg>
);

export function UserMenuDropdown() {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isLeaderboardOpen, setIsLeaderboardOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isMainButtonAnimated, setIsMainButtonAnimated] = useState(false);
  const [isProfileButtonAnimated, setIsProfileButtonAnimated] = useState(false);
  const [isLeaderboardButtonAnimated, setIsLeaderboardButtonAnimated] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { width } = useWindowSize();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLeaderboardClick = () => {
    setIsLeaderboardOpen(true);
    setIsDropdownOpen(false);
    setIsLeaderboardButtonAnimated(true);
    setIsProfileButtonAnimated(false);
    setIsMainButtonAnimated(false);
  };

  const handleProfileClick = () => {
    setIsProfileOpen(true);
    setIsDropdownOpen(false);
    setIsProfileButtonAnimated(true);
    setIsLeaderboardButtonAnimated(false);
    setIsMainButtonAnimated(false);
  };

  const handleMainButtonClick = () => {
    setIsDropdownOpen(!isDropdownOpen);
    if (!isDropdownOpen) {
      setIsMainButtonAnimated(true);
    }
  };

  // Сброс анимаций при закрытии модальных окон
  const handleLeaderboardClose = () => {
    setIsLeaderboardOpen(false);
    setIsLeaderboardButtonAnimated(false);
  };

  const handleProfileClose = () => {
    setIsProfileOpen(false);
    setIsProfileButtonAnimated(false);
  };

  // Сброс анимаций при закрытии выпадающего списка
  useEffect(() => {
    if (!isDropdownOpen) {
      setIsMainButtonAnimated(false);
      setIsProfileButtonAnimated(false);
      setIsLeaderboardButtonAnimated(false);
    }
  }, [isDropdownOpen]);

  return (
    <>
      <div className="relative" ref={dropdownRef}>
        <button 
          onClick={handleMainButtonClick}
          onMouseEnter={() => setIsMainButtonAnimated(true)}
          onMouseLeave={() => !isDropdownOpen && setIsMainButtonAnimated(false)}
          className="flex items-center gap-1 sm:gap-1 btn-textured px-3 sm:px-4 md:px-5 py-2 sm:py-2 md:py-2.5 text-white text-xs sm:text-sm md:text-base border-2 border-purple-500/80"
        >
          <AnimatedHubIcon isOpen={isMainButtonAnimated} className="w-4 h-4 sm:w-5 sm:h-5" />
          <span className="mr-1">Hub</span>
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}>
            <polyline points="6 9 12 15 18 9"></polyline>
          </svg>
        </button>
        
        <div
          className="absolute mt-2 w-48 bg-black/80 backdrop-blur-sm rounded-xl shadow-xl z-50 border-2 border-purple-800/50 overflow-hidden"
          style={{
            transform: isDropdownOpen
              ? (width >= 768 ? 'translateY(0) translateX(-50%)' : 'translateY(0)')
              : (width >= 768 ? 'translateY(-10px) translateX(-50%)' : 'translateY(-10px)'),
            transition: 'transform 0.15s ease-out, opacity 0.15s ease-out',
            opacity: isDropdownOpen ? 1 : 0,
            pointerEvents: isDropdownOpen ? 'auto' : 'none',
            right: width < 768 ? '0' : 'auto',
            left: width >= 768 ? '50%' : 'auto'
          }}
        >
            <div className="p-1.5 space-y-1.5">
              <button
                onClick={handleProfileClick}
                onMouseEnter={() => setIsProfileButtonAnimated(true)}
                onMouseLeave={() => !isProfileOpen && setIsProfileButtonAnimated(false)}
                className="btn-dropdown-glass !text-white !px-4 !py-2 !text-sm flex items-center gap-1 w-full animate-fadeIn opacity-0"
                style={{ animationDelay: '0.02s', animationFillMode: 'forwards' }}
              >
                <AnimatedUserIcon isAnimated={isProfileButtonAnimated} className="w-4 h-4 mr-1.5" />
                <span>Profile</span>
              </button>
              <div className="mx-2 border-t border-purple-600/30"></div>
              <button
                onClick={handleLeaderboardClick}
                onMouseEnter={() => setIsLeaderboardButtonAnimated(true)}
                onMouseLeave={() => !isLeaderboardOpen && setIsLeaderboardButtonAnimated(false)}
                className="btn-dropdown-glass !text-white !px-4 !py-2 !text-sm flex items-center gap-1 w-full animate-fadeIn opacity-0"
                style={{ animationDelay: '0.04s', animationFillMode: 'forwards' }}
              >
                <AnimatedLeaderboardIcon isAnimated={isLeaderboardButtonAnimated} className="w-4 h-4 mr-1.5" />
                <span>Leaderboard</span>
              </button>
            </div>
          </div>
      </div>
      
      <LeaderboardModal isOpen={isLeaderboardOpen} onClose={handleLeaderboardClose} />
      <ProfileModal isOpen={isProfileOpen} onClose={handleProfileClose} />
    </>
  );
}
